import { z } from 'zod';
import { ModuleManifest } from '@momentum/shared';
import { DECISION_MAKER_ID } from '../decision-maker/manifest.ts';
import { POC_CHOICE_ID } from '../poc-choice/manifest.ts';
import { HOT_POTATO_ID } from '../hot-potato/manifest.ts';

/**
 * The expanded world (D24/D25) is a separate module id, not a new graph under `momentum-board`:
 * persisted `positions` are plain space-id strings that the state schema cannot check against the
 * compiled-in graph, so swapping `section.json` under a running module would crash `travel()` on a
 * stale id instead of quarantining the session. See docs/concept/world-v2/PROPOSAL.md §3.
 * `stateVersion` stays 1 because later additions are optional and old snapshots remain valid.
 */
export const MOMENTUM_BOARD_V2_ID = 'momentum-board-v2';
export const momentumBoardV2Manifest = ModuleManifest.parse({
  id: MOMENTUM_BOARD_V2_ID, version: '0.3.0', name: 'Momentum world',
  description: 'The expanded ceramic world: 28 route spaces, six forks, a crystal pedestal island and a rear region behind the water arch.',
  disposable: false, players: { min: 2, max: 6 }, participation: 'individual', teams: null,
  voice: { scope: 'shared' },
  capabilities: { launchesChildren: true, childModuleIds: [POC_CHOICE_ID, DECISION_MAKER_ID, HOT_POTATO_ID], usesRandom: true, hasPrivateData: false, renderer: 'board' },
  launchableFromLobby: true,
});
export const MomentumBoardV2Config = z.object({
  activity: z.enum([DECISION_MAKER_ID, POC_CHOICE_ID, HOT_POTATO_ID]).optional(),
  /** Meeting/demo flow: launch an eligible shared activity after every complete roster round. */
  meetingDemo: z.literal(true).optional(),
});
export type MomentumBoardV2Config = z.infer<typeof MomentumBoardV2Config>;
export const MomentumBoardV2Action = z.discriminatedUnion('type', [
  z.object({ type: z.literal('roll'), turnId: z.string() }).strict(),
  z.object({ type: z.literal('route'), turnId: z.string(), rollId: z.string(), from: z.string(), to: z.string() }).strict(),
]);
export type MomentumBoardV2Action = z.infer<typeof MomentumBoardV2Action>;
